import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { Avatar, Card, Meter } from '../components/ui'
import { Footer } from '../components/Footer'
import { GhostMark } from '../components/GhostMark'
import { AlertBell } from '../components/AlertBell'
import { AlertToasts } from '../components/AlertToasts'
import { AnnouncementBanner } from '../components/AnnouncementBanner'
import { BlackBadge } from '../components/BlackBadge'
import { planById } from '../lib/constants'
import { isValidDate, shortDate } from '../lib/format'
import {
  IconActivity,
  IconAdmin,
  IconBilling,
  IconCandidates,
  IconClose,
  IconLogout,
  IconSend,
  IconSettings,
  IconTeam,
} from '../components/icons'
import { canAccessAdmin } from '../lib/permissions'
import './shell.css'

const LINKS = [
  { to: '/candidates', label: 'Candidates', Icon: IconCandidates },
  { to: '/verify', label: 'Send requests', Icon: IconSend },
  { to: '/activity', label: 'Activity', Icon: IconActivity },
  { to: '/team', label: 'Team', Icon: IconTeam },
  { to: '/billing', label: 'Billing', Icon: IconBilling },
  { to: '/settings', label: 'Settings', Icon: IconSettings },
]

// Platform-staff-only section, appended to the nav when the signed-in recruiter
// has `isStaff` (see canAccessAdmin). Hidden entirely for customers.
const ADMIN_LINK = { to: '/admin', label: 'Admin', Icon: IconAdmin }

export function Shell() {
  const { session, billing, allowanceUsed, allowanceTotal, logout } = useStore()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)

  // Mobile drawer: while the sidebar is open, let Escape dismiss it and lock the
  // page behind it from scrolling (it's a fixed overlay below 860px). No-op on
  // desktop, where the sidebar is always-on and `open` stays false.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open])

  const plan = planById(billing.planId)
  const unlimited = !Number.isFinite(allowanceTotal)
  const isBlack = billing.planId === 'black'
  const { recruiter } = session

  function doLogout() {
    logout()
    nav('/login')
  }

  return (
    <div className="shell">
      <aside id="nav-sidebar" className={`nav${open ? ' is-open' : ''}`}>
        <div className="nav__brand">
          <GhostMark size={34} glow />
          <span className="nav__wordmark">
            Vouchr
            <small>Recruiter Console</small>
          </span>
          <AlertBell />
          <button
            className="nav__close"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            title="Close menu"
          >
            <IconClose size={20} />
          </button>
        </div>

        <nav className="nav__links" onClick={() => setOpen(false)}>
          {(canAccessAdmin(recruiter) ? [...LINKS, ADMIN_LINK] : LINKS).map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
            >
              <Icon size={19} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="nav__spacer" />

        <Card inset className="nav__allowance">
          <div className="nav__allowance-top">
            {isBlack ? <BlackBadge sm /> : <span className="nav__tier">{plan.name} plan</span>}
            <GhostMark size={20} tint={isBlack ? 'founding' : 'vault'} />
          </div>
          <Meter
            label="Verifications"
            used={allowanceUsed}
            total={unlimited ? allowanceUsed : allowanceTotal}
            unit={unlimited ? 'unlimited' : 'this month'}
            sm
            foot={
              unlimited
                ? 'Unlimited on your plan'
                : allowanceUsed >= allowanceTotal
                  ? 'Allowance reached — overage applies'
                  : isValidDate(billing.renewsOn)
                    ? `Renews ${shortDate(billing.renewsOn)}`
                    : `${allowanceTotal - allowanceUsed} left this month`
            }
          />
        </Card>

        <div className="nav__me" onClick={() => nav('/settings')}>
          <Avatar hue={recruiter.avatarHue} initials={recruiter.initials} size={36} />
          <div className="nav__me-text">
            <div className="nav__me-name">{recruiter.name}</div>
            <div className="nav__me-sub">{recruiter.company}</div>
          </div>
          <button
            className="nav__logout"
            onClick={(e) => {
              e.stopPropagation()
              doLogout()
            }}
            aria-label="Sign out"
            title="Sign out"
          >
            <IconLogout size={17} />
          </button>
        </div>
      </aside>

      {open && <div className="nav__scrim" onClick={() => setOpen(false)} />}

      <div className="main">
        <div className="topbar">
          <button
            className="topbar__burger"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="nav-sidebar"
          >
            ☰
          </button>
          <GhostMark size={26} glow />
          <span className="nav__wordmark" style={{ fontSize: '0.95rem' }}>
            Vouchr
          </span>
        </div>
        <main className="main">
          <AnnouncementBanner />
          <Outlet />
        </main>
        <Footer />
      </div>

      <AlertToasts />
    </div>
  )
}
